import React from "react";

/*
  Mitarbeitenden-Badge: runder Kreis mit den Initialen einer Person.
  Wird quer durch die App verwendet (Dashboard, Verfügbarkeit, Schichtplan,
  Team) und ersetzt die vorher pro Szene kopierten Avatar-Spans.

  Optional umgibt ein Fortschrittsring den Kreis (Auslastung): grün bis zum
  Soll, rot sobald darüber. Der Ring liegt bewusst *ausserhalb* des Kreises,
  damit die Initialen immer gleich gross bleiben.
*/

export const PERSON_BADGE_SIZES = ["xs", "sm", "md", "lg", "xl"];
export const PERSON_BADGE_TONES = ["neutral", "solid", "brand", "custom"];

export function personInitials(name, { max = 2 } = {}) {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "–";
  if (parts.length === 1) {
    const single = parts[0];
    return (max > 1 ? single.slice(0, 2) : single.slice(0, 1)).toLocaleUpperCase("de");
  }

  return [parts[0][0], parts[parts.length - 1][0]]
    .filter(Boolean)
    .slice(0, max)
    .join("")
    .toLocaleUpperCase("de");
}

// 0..1 → Prozent auf der pathLength-normierten Kreisbahn (100).
const toRingPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return Math.min(100, numeric * 100);
};

function ProgressRing({ progress, overage, tone }) {
  const fill = toRingPercent(progress);
  const over = toRingPercent(overage);
  const isOver = tone === "over";

  return (
    <svg className="ds-person-badge__ring" viewBox="0 0 36 36" focusable="false" aria-hidden="true">
      <circle className="ds-person-badge__ring-track" cx="18" cy="18" r="16" pathLength="100" />
      {tone !== "neutral" && (fill > 0 || isOver) ? (
        <circle
          className="ds-person-badge__ring-fill"
          cx="18"
          cy="18"
          r="16"
          pathLength="100"
          strokeDasharray={`${isOver ? 100 : fill} 100`}
          transform="rotate(-90 18 18)"
        />
      ) : null}
      {isOver && over > 0 ? (
        <circle
          className="ds-person-badge__ring-overage"
          cx="18"
          cy="18"
          r="16"
          pathLength="100"
          strokeDasharray={`${over} 100`}
          transform="rotate(-90 18 18)"
        />
      ) : null}
    </svg>
  );
}

export default function AppPersonBadge({
  name = "",
  initials: initialsProp,
  size = "md",
  tone = "neutral",
  color,
  icon = null,
  // Fortschrittsring
  showProgress = false,
  progress = 0,
  overage = 0,
  progressTone,
  // Interaktion
  onClick,
  ariaLabel,
  title,
  className = "",
  ...props
}) {
  const label = initialsProp ?? personInitials(name);
  const interactive = typeof onClick === "function";
  const resolvedProgressTone = progressTone
    ?? (Number(progress) > 1 || Number(overage) > 0
      ? "over"
      : Number(progress) >= 1
        ? "complete"
        : Number(progress) > 0
          ? "partial"
          : "neutral");

  const classes = [
    "ds-person-badge",
    `ds-person-badge--${size}`,
    `ds-person-badge--${tone}`,
    showProgress ? "ds-person-badge--with-ring" : "",
    showProgress ? `ds-person-badge--progress-${resolvedProgressTone}` : "",
    interactive ? "ds-person-badge--interactive" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const style = tone === "custom" && color ? { "--ds-person-badge-color": color } : undefined;
  const Component = interactive ? "button" : "span";
  const interactiveProps = interactive
    ? {
        type: "button",
        "aria-label": ariaLabel || name || undefined,
        onClick: (event) => {
          event.stopPropagation();
          onClick(event);
        },
      }
    : { "aria-hidden": ariaLabel ? undefined : "true", "aria-label": ariaLabel || undefined };

  return (
    <Component className={classes} style={style} title={title} {...interactiveProps} {...props}>
      {showProgress ? (
        <ProgressRing progress={progress} overage={overage} tone={resolvedProgressTone} />
      ) : null}
      <span className="ds-person-badge__label">{icon || label}</span>
    </Component>
  );
}
